import React from 'react';
import Link from 'next/link';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

export const MiniSectionHeading = ({
  title, href, ctaLabel,
}: { title: string; href?: string; ctaLabel?: string }) => (
  <div className="mb-2.5 flex items-baseline justify-between gap-3">
    <Heading level={3} className="font-heading text-lg">{title}</Heading>
    {href && ctaLabel && (
      <Link href={href} className="shrink-0 text-sm font-semibold text-primary hover:underline">
        {`${ctaLabel} »`}
      </Link>
    )}
  </div>
);

// v3 StatBlock atom: small-caps label on top, big tabular-nums value
// underneath. Matches the "Lifts open / Trails open / …" treatment from
// the redesign prototypes.
export const StatBlock = ({
  label, value, unit,
}: { label: string; value: string | number; unit?: string }) => (
  <div>
    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
      {label}
    </div>
    <div className="mt-1 inline-flex items-baseline gap-1.5 font-heading text-2xl font-bold leading-none tabular-nums sm:text-3xl">
      <span>{value}</span>
      {unit && (
        <span className="text-sm font-medium text-muted-foreground">{unit}</span>
      )}
    </div>
  </div>
);

// Out-of-season notice that sits between SEO intro and live sections
// when the resort is fully closed.
export const ClosedBanner = ({
  closedLabel, resortName, projectedOpening,
}: { closedLabel: string; resortName: string; projectedOpening: string | null }) => {
  const { t } = useOTSTranslation('common');
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
      <Text variant="default" className="text-sm">
        <span className="text-xs font-bold uppercase tracking-widest text-amber-700">
          {closedLabel}
        </span>
        <span className="mx-2 text-amber-700/40">·</span>
        <span className="font-semibold text-amber-900">
          {t('resort.skiReport.closedForSeason', { resort: resortName })}
        </span>
        {projectedOpening && (
          <>
            {' '}
            <span className="text-amber-900/80">
              {t('resort.skiReport.projectedReopening', { date: projectedOpening })}
            </span>
          </>
        )}
      </Text>
    </div>
  );
};
